import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import { apiFetch, ApiError } from "@/lib/api";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { BOM_TEST_SYNC_KEY, parseBomTestSync, writeBomTestSync, type BomTestSyncPayload } from "@/lib/bomTestSync";
import { BomTestPanel } from "./BomShared";

function parseId(value: string | null, defaultValue: number): number {
  if (value == null || value === "") {
    return defaultValue;
  }
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? defaultValue : n;
}

/**
 * Detached “test only” view for a template revision, linked to Set BoM rules via URL + localStorage.
 */
export function BomTestRevisionPage() {
  const [search, setSearch] = useSearchParams();
  const hubWritesAllowed = useHubModuleWrite("bom");
  const bomReadOnlyTitle = "Your role has read-only access to the BoM management hub module.";

  const initialTemplate = parseId(search.get("templateId"), 0);
  const initialRevision = parseId(search.get("revision"), 0);
  const initialSku = search.get("testSku") ?? "";

  const [templateId, setTemplateId] = useState(initialTemplate);
  const [revision, setRevision] = useState(initialRevision);
  const [testSku, setTestSku] = useState(initialSku);

  const applyPayload = useCallback(
    (payload: BomTestSyncPayload) => {
      setTemplateId(payload.templateId);
      setRevision(payload.revision);
      setTestSku(payload.testSku);
      setSearch(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("templateId", String(payload.templateId));
          next.set("revision", String(payload.revision));
          if (payload.testSku) {
            next.set("testSku", payload.testSku);
          } else {
            next.delete("testSku");
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearch],
  );

  useEffect(() => {
    if (templateId > 0 && revision > 0) {
      writeBomTestSync({ templateId, revision, testSku });
    }
  }, [templateId, revision, testSku]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== BOM_TEST_SYNC_KEY || !event.newValue) {
        return;
      }
      const payload = parseBomTestSync(event.newValue);
      if (!payload) {
        return;
      }
      applyPayload(payload);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [applyPayload]);

  const testSkusQuery = useQuery({
    queryKey: ["bom", "testSkus", templateId],
    queryFn: () =>
      apiFetch<{ skus: { value: string; label: string }[] }>("/api/bom/test-skus", {
        query: { templateId },
      }),
    enabled: templateId > 0,
  });

  const initialQuery = useQuery({
    queryKey: ["bom", "rows", "initial", templateId, revision],
    queryFn: () =>
      apiFetch<{ rows: Record<string, unknown>[] }>("/api/bom/rows/initial", {
        query: { templateId, revision },
      }),
    enabled: templateId > 0 && revision > 0,
    staleTime: 20_000,
    gcTime: 5 * 60_000,
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      if (templateId <= 0 || revision <= 0 || !testSku.trim()) {
        throw new Error("Template, revision, and test SKU are required. Open this window from Set BoM rules or set query parameters.");
      }
      const [parseResult, specResult] = await Promise.all([
        apiFetch<{ rows: unknown[] }>("/api/bom/preview/parse-sku-specific", {
          query: { sku: testSku, templateId, revision, useCache: "true" },
        }),
        apiFetch<{ rows: unknown[] }>("/api/bom/preview/bom-specific", {
          query: { sku: testSku, templateId, revision },
        }),
      ]);
      return { parse: parseResult, spec: specResult };
    },
  });

  const parseQueryState = {
    data: testMutation.data?.parse,
    error: (testMutation.error as ApiError | null) ?? null,
    isFetching: testMutation.isPending,
  };
  const specificQueryState = {
    data: testMutation.data?.spec,
    error: (testMutation.error as ApiError | null) ?? null,
    isFetching: testMutation.isPending,
  };

  const onTestSku = (value: string): void => {
    setTestSku(value);
    if (templateId > 0 && revision > 0) {
      writeBomTestSync({ templateId, revision, testSku: value });
    }
  };

  if (templateId <= 0 || revision <= 0) {
    return (
      <div className="max-w-2xl space-y-3 text-base text-slate-700">
        <h1 className="text-xl font-semibold text-slate-900">Test revision (detached)</h1>
        <p>
          This page needs <code className="rounded bg-slate-100 px-1.5 text-sm">templateId</code> and{" "}
          <code className="rounded bg-slate-100 px-1.5 text-sm">revision</code> in the URL, or open it with{" "}
          <span className="font-medium">Open tests in new window</span> from Set BoM rules.
        </p>
        <p>
          <Link to="/bom/set-bom-rules" className="text-brand-700 underline">
            Go to Set BoM rules
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <PageTitleWithHelp hoverHint={`Stays in sync with Set BoM rules for template ${String(templateId)}, revision ${revision} (and test SKU) while both tabs are open. Change selections on either side to update the other.`}>
            Test revision (new window)
          </PageTitleWithHelp>
        </div>
        <Link
          to="/bom/set-bom-rules"
          className="inline-flex h-10 items-center justify-center rounded-md border border-slate-300 bg-white px-4 text-sm font-medium text-slate-900 hover:bg-slate-100"
        >
          Back to Set BoM rules
        </Link>
      </div>

      <BomTestPanel
        testSku={testSku}
        onTestSku={onTestSku}
        onRunTest={() => {
          if (!hubWritesAllowed) return;
          testMutation.mutate();
        }}
        running={testMutation.isPending}
        skuOptions={testSkusQuery.data?.skus ?? []}
        parseQuery={parseQueryState}
        specificQuery={specificQueryState}
        initialRows={initialQuery.data?.rows}
        density="comfortable"
        runTestBlocked={!hubWritesAllowed}
        runTestBlockedTitle={bomReadOnlyTitle}
      />
      {testMutation.isError ? (
        <p className="text-base text-rose-700">{(testMutation.error as Error).message}</p>
      ) : null}
    </div>
  );
}
